import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Modal, Table, Tag, Badge, Button, Tooltip } from 'antd';
import {
    UserOutlined,
    BarChartOutlined,
    WarningOutlined,
    PhoneOutlined,
    MailOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import config from '../../config';
import AdminLayout from '../Layout/AdminLayout';
import ExportStudentProfiles from './ExportStudentProfiles';
import RegistrationStatistics from './RegistrationStatistics';

const AdminDashboard = () => {
    const [statistics, setStatistics] = useState({
        totalStudents: 0,
        totalAdmissions: 0,
        admissionsByArea: [],
        admissionsByGroup: [],
        totalStudentsByFirstAdmissionId: [],
        totalStudentsWithIelts: 0,
        totalStudentsWithProvincialPrize: 0,
        studentsWithoutNV1Count: 0,
    });
    const [loading, setLoading] = useState(true);
    const [withoutNV1Modal, setWithoutNV1Modal] = useState(false);
    const [withoutNV1Students, setWithoutNV1Students] = useState([]);
    const [withoutNV1Loading, setWithoutNV1Loading] = useState(false);
    const navigate = useNavigate();

    const authHeader = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${config.apiUrl}/api/v1/admin/dashboard-statistics`,
                authHeader()
            );
            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            if (error.response?.status === 401) {
                message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentsWithoutNV1 = async () => {
        try {
            setWithoutNV1Loading(true);
            const response = await axios.get(
                `${config.apiUrl}/api/v1/admin/students-without-nv1`,
                authHeader()
            );
            if (response.data.success) {
                setWithoutNV1Students(response.data.data.students || []);
            }
        } catch (error) {
            message.error('Không thể tải danh sách thí sinh chưa chọn NV1');
        } finally {
            setWithoutNV1Loading(false);
        }
    };

    const openWithoutNV1Modal = () => {
        setWithoutNV1Modal(true);
        fetchStudentsWithoutNV1();
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const withoutNV1Columns = [
        {
            title: 'Mã TS',
            dataIndex: 'student_code',
            key: 'student_code',
            width: 100,
            render: (v) => v || <span style={{ color: '#bbb' }}>—</span>,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            // Lấy từ users.name (đăng ký) vì profile chưa được submit
            render: (v) => v || <span style={{ color: '#bbb' }}>Chưa điền</span>,
        },
        {
            title: 'CCCD',
            dataIndex: 'identification',
            key: 'identification',
            // CCCD chỉ có sau khi submit form hồ sơ — sẽ trống với nhóm này
            render: (v) => v || (
                <Tooltip title="Thí sinh chưa submit hồ sơ nên CCCD chưa được điền">
                    <span style={{ color: '#bbb' }}>Chưa có</span>
                </Tooltip>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email, record) => (
                <span>
                    <MailOutlined style={{ marginRight: 4, color: '#1677ff' }} />
                    {email}
                    {record.email_verified
                        ? <Tooltip title="Email đã xác thực"><CheckCircleOutlined style={{ marginLeft: 6, color: '#52c41a' }} /></Tooltip>
                        : <Tooltip title="Email chưa xác thực"><ExclamationCircleOutlined style={{ marginLeft: 6, color: '#faad14' }} /></Tooltip>
                    }
                </span>
            ),
        },
        {
            title: 'SĐT thí sinh',
            dataIndex: 'student_phone_number',
            key: 'student_phone_number',
            // Lấy từ users.phone (đăng ký) nếu chưa có trong profile
            render: (v) => v ? (
                <span><PhoneOutlined style={{ marginRight: 4 }} />{v}</span>
            ) : <span style={{ color: '#bbb' }}>—</span>,
        },
        {
            title: 'SĐT phụ huynh',
            dataIndex: 'parents_phone_number',
            key: 'parents_phone_number',
            render: (v) => v ? (
                <span><PhoneOutlined style={{ marginRight: 4 }} />{v}</span>
            ) : <span style={{ color: '#bbb' }}>—</span>,
        },
        {
            title: 'Ngày đăng ký',
            dataIndex: 'registered_at',
            key: 'registered_at',
            width: 140,
        },
    ];

    const dashboardContent = (
        loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        ) : (
            <>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <ExportStudentProfiles />
                </div>

                {/* Summary statistic cards */}
                <Row gutter={16}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Tổng số thí sinh"
                                value={statistics?.totalStudents}
                                prefix={<UserOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Tổng số nguyện vọng (NV2+)"
                                value={statistics?.totalAdmissions}
                                prefix={<BarChartOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card hoverable onClick={() => navigate('/admin/students-ielts')} style={{ cursor: 'pointer' }}>
                            <Statistic
                                title="Thí sinh có điểm IELTS"
                                value={statistics?.totalStudentsWithIelts}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card hoverable onClick={() => navigate('/admin/students-provincial-prize')} style={{ cursor: 'pointer' }}>
                            <Statistic
                                title="Thí sinh có Giải thưởng cấp tỉnh"
                                value={statistics?.totalStudentsWithProvincialPrize}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Charts */}
                <Row gutter={16} style={{ marginTop: '24px' }}>
                    <Col span={12}>
                        <Card title="Thống kê theo khu vực">
                            {statistics.admissionsByArea?.length > 0 ? (
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <BarChart
                                            data={statistics.admissionsByArea}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="area" />
                                            <YAxis />
                                            <RechartsTooltip />
                                            <Legend />
                                            <Bar dataKey="count" name="Số lượng" fill="#8884d8" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#bbb', padding: '80px 0' }}>
                                    Chưa có dữ liệu
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card
                            title="Thống kê theo đối tượng"
                            extra={
                                statistics.admissionsByGroup?.length === 0 &&
                                <Tooltip title="Trường 'đối tượng' không bắt buộc trong form — hầu hết thí sinh chưa điền">
                                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                </Tooltip>
                            }
                        >
                            {statistics.admissionsByGroup?.length > 0 ? (
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <BarChart
                                            data={statistics.admissionsByGroup}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="group" />
                                            <YAxis />
                                            <RechartsTooltip />
                                            <Legend />
                                            <Bar dataKey="count" name="Số lượng" fill="#82ca9d" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#bbb', padding: '80px 0' }}>
                                    Chưa có dữ liệu (trường đối tượng không bắt buộc)
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col span={24} style={{ marginTop: '16px' }}>
                        <Card title="Thống kê theo Nguyện vọng 1 (NV1)">
                            {statistics.totalStudentsByFirstAdmissionId?.length > 0 ? (
                                <div style={{ width: '100%', height: Math.max(300, statistics.totalStudentsByFirstAdmissionId.length * 36) }}>
                                    <ResponsiveContainer>
                                        <BarChart
                                            layout="vertical"
                                            data={statistics.totalStudentsByFirstAdmissionId}
                                            margin={{ top: 5, right: 40, left: 8, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" allowDecimals={false} />
                                            <YAxis
                                                type="category"
                                                dataKey="subject_name"
                                                width={220}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <RechartsTooltip />
                                            <Legend verticalAlign="top" />
                                            <Bar dataKey="count" name="Số thí sinh đăng ký NV1" fill="#262662" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#bbb', padding: '80px 0' }}>
                                    Chưa có dữ liệu
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginTop: '24px' }}>
                    <Col span={24}>
                        <RegistrationStatistics />
                    </Col>
                </Row>

                {/* Modal: danh sách thí sinh chưa chọn NV1 */}
                <Modal
                    title={
                        <span>
                            <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                            Thí sinh chưa chọn NV1
                            <Badge
                                count={withoutNV1Students.length}
                                style={{ backgroundColor: '#ff4d4f', marginLeft: 12 }}
                                overflowCount={9999}
                            />
                        </span>
                    }
                    open={withoutNV1Modal}
                    onCancel={() => setWithoutNV1Modal(false)}
                    footer={[
                        <Button key="close" onClick={() => setWithoutNV1Modal(false)}>
                            Đóng
                        </Button>
                    ]}
                    width={1100}
                    styles={{ body: { padding: '16px 0' } }}
                >
                    <p style={{ color: '#888', marginBottom: 12 }}>
                        Các thí sinh dưới đây đã tạo tài khoản và xác thực email nhưng chưa hoàn thiện hồ sơ (chưa chọn ngành NV1).
                        Vui lòng liên hệ tư vấn để hỗ trợ.
                    </p>
                    <Table
                        columns={withoutNV1Columns}
                        dataSource={withoutNV1Students}
                        rowKey={(r, i) => r.student_code || r.email || i}
                        loading={withoutNV1Loading}
                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Tổng ${t} thí sinh` }}
                        scroll={{ x: 900 }}
                        size="small"
                    />
                </Modal>
            </>
        )
    );

    return (
        <AdminLayout title="Tổng quan">
            {dashboardContent}
        </AdminLayout>
    );
};

export default AdminDashboard;
